import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import Logo from "../../components/Logo/Logo";
import { Eye, EyeOff } from "lucide-react";
import { isValidEmail, isRequired } from "../../utils/validation";
import { useAuth } from "../../hooks";
import { useDispatch } from "react-redux";
import { setEmail, setPlanId, setAdsEnabled } from "../../redux/slices/userProfileSlice";
import { IDLE_LOGOUT_REASON_KEY } from "../../components/common/IdleTimeoutManager";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, error, login, clearError, data } = useAuth();

  const [email, setEmailLocal] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const isFormValid = isValidEmail(email) && isRequired(password);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  useEffect(() => {
    let reason: string | null = null;
    try {
      reason = sessionStorage.getItem(IDLE_LOGOUT_REASON_KEY);
      sessionStorage.removeItem(IDLE_LOGOUT_REASON_KEY);
    } catch {
      // ignore
    }

    if (reason === 'idle') {
      setLocalError("You've been signed out due to inactivity. Please log in again.");
    } else if (reason?.startsWith('disabled:')) {
      setLocalError(reason.slice('disabled:'.length));
    }
  }, []);

  useEffect(() => {
    if (data) {
      if (data.planId !== undefined) {
        dispatch(setPlanId(data.planId));
      }
      if (data.subscriptionPlan?.adsEnabled !== undefined) {
        dispatch(setAdsEnabled(data.subscriptionPlan.adsEnabled));
      }
    }
  }, [data, dispatch]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isLoading) return;
    
    setLocalError(null);
    clearError();
    
    try {
      const result = await login({ email, password });
      if (result.success) {
        dispatch(setEmail(email));
        
        if (result.data) {
          if (result.data.planId !== undefined) {
            dispatch(setPlanId(result.data.planId));
          }
          if (result.data.subscriptionPlan?.adsEnabled !== undefined) {
            dispatch(setAdsEnabled(result.data.subscriptionPlan.adsEnabled));
          }
        }
        
        navigate("/dashboard");
      } else {
        let errorMessage = 'Login failed. Please try again.';
        
        if (result?.error?.code === 'auth/user-not-found') {
          errorMessage = 'No account found with this email. Please sign up first.';
        } else if (result?.error?.code === 'auth/wrong-password') {
          errorMessage = 'Incorrect password. Please try again or use "Forgot password".';
        } else if (result?.error?.code === 'auth/invalid-email') {
          errorMessage = 'Invalid email address. Please check and try again.';
        } else if (result?.error?.code === 'auth/user-disabled') {
          errorMessage = 'This account has been disabled. Please contact support.';
        } else if (result?.error?.code === 'auth/too-many-requests') {
          errorMessage = 'Too many failed login attempts. Please try again later.';
        } else if (result?.error?.code === 'auth/invalid-credential') {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        } else if (result?.error?.message) {
          errorMessage = result.error.message;
        }
        
        setLocalError(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error?.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setLocalError(errorMessage);
    }
  };

  const onNavigateToSignup = () => {
    navigate("/create-account");
  };

  const onNavigateToForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="flex min-h-screen">

      {/* ── Left brand panel (desktop only) ── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-brand-primary to-brand-gray p-12 text-white">
        <div className="max-w-xs text-center">
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
            <svg viewBox="0 0 24 24" className="h-8 w-8 fill-white" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Z" />
              <path d="M12 6a1 1 0 0 0-1 1v5.59l-3.71 3.7a1 1 0 1 0 1.42 1.42l4-4A1 1 0 0 0 13 13V7a1 1 0 0 0-1-1Z" />
            </svg>
          </div>
          <h2 className="mb-3 text-3xl font-bold">Welcome back</h2>
          <p className="text-base leading-relaxed text-white/75">
            Connect with IT professionals and manage your availability effortlessly.
          </p>
          <div className="mt-10 flex justify-center gap-2">
            <div className="h-1.5 w-8 rounded-full bg-white/50" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
            <div className="h-1.5 w-2 rounded-full bg-white/30" />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-sm">

          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <div className="w-36 sm:w-44">
              <Logo />
            </div>
          </div>

          <h1 className="mb-1 text-2xl font-bold text-gray-950">Sign in</h1>
          <p className="mb-8 text-sm text-gray-600">Enter your credentials to continue.</p>

          <form onSubmit={handleLogin} className="space-y-4">

            {/* Email */}
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Email
              </label>
              <Input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmailLocal(e.target.value); setLocalError(null); }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                required
              />
            </div>

            {/* Password */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Password
                </label>
                <button
                  type="button"
                  onClick={onNavigateToForgotPassword}
                  className="text-xs font-semibold text-brand-primary transition-colors hover:text-brand-hover"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setLocalError(null); }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-10 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Error banner */}
            {(localError || error) && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {localError || error?.message}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="inline-flex w-full items-center justify-center rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {isLoading ? "Logging in..." : "Continue"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={onNavigateToSignup}
              className="font-semibold text-brand-primary transition-colors hover:text-brand-hover"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
